import styled from 'styled-components';
import { Card, CardContent, Box, Typography, Divider } from '@mui/material';

export const StyledCard = styled(Card)`
  height: 100%;
  min-height: 340px;
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  display: flex;
  flex-direction: column;
  position: relative;
  cursor: pointer;
  border-radius: 12px !important;
  border: 1px solid #eef0f4;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(13, 78, 254, 0.1);
    border-color: #d8e0ee;
  }
`;

export const StarContainer = styled(Box)`
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  padding: 6px;
  background-color: rgba(255, 255, 255, 0.9);
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(13, 78, 254, 0.08);
  }
`;

export const StyledCardContent = styled(CardContent)`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 18px !important;
`;

export const BusinessHeader = styled(Box)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 10px;
  padding-right: 36px;
`;

export const BusinessInfo = styled(Box)`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const BusinessPhoto = styled(Box)`
  width: 120px;
  height: 120px;
  border-radius: 10px;
  overflow: hidden;
  position: relative;
  border: 1px solid #e3e8ee;
  flex-shrink: 0;
  background-color: #fafbfc;
`;

export const BusinessName = styled(Typography)`
  font-size: 1.05rem;
  font-weight: 700;
  color: #1a1f36;
  line-height: 1.3;
  margin-bottom: 2px;

  /* clamp to 2 lines so long names don't blow out the card */
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/* Shared row style for location/email/phone/rating — consistent size + color */
const contactRowStyles = `
  display: flex;
  align-items: center;
  gap: 6px;

  .MuiTypography-root {
    font-size: 0.8rem;
    line-height: 1.35;
    color: #5a6478;
    word-break: break-word;
    min-width: 0;
  }

  .MuiSvgIcon-root {
    font-size: 14px;
    color: #8a93a6;
    flex-shrink: 0;
  }
`;

export const LocationInfo = styled(Box)`
  ${contactRowStyles}
`;

export const EmailInfo = styled(Box)`
  ${contactRowStyles}
`;

/* Rating row — same scale as the contact rows so it doesn't shout */
export const RatingRow = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;

  .MuiSvgIcon-root {
    font-size: 15px;
  }

  .MuiTypography-root {
    font-size: 0.78rem;
    color: #5a6478;
    margin-left: 4px;
    font-weight: 500;
  }
`;

export const StyledDivider = styled(Divider)`
  margin: 12px 0 10px !important;
  border-color: #eef0f4 !important;
`;

export const ServicesTitle = styled(Typography)`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-weight: 700;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #1a1f36;

  .MuiSvgIcon-root {
    font-size: 15px;
    color: #0d4efe;
  }
`;

export const ServicesList = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 12px;
`;

/* Pill-style trade chips instead of bulleted list */
export const ServiceItem = styled(Typography)`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(13, 78, 254, 0.08), rgba(1, 235, 178, 0.08));
  color: #0d4efe;
  border: 1px solid rgba(13, 78, 254, 0.18);
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 0.75rem !important;
  font-weight: 600;
  line-height: 1.4;

  /* When the component renders the empty-state message, drop the pill styling */
  &.empty {
    background: none;
    border: none;
    padding: 0;
    color: #8a93a6;
    font-weight: 400;
  }
`;

export const ShowMoreLink = styled(Typography)`
  color: #0d4efe;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;
  font-size: 0.78rem;

  &:hover {
    color: #01ebb2;
  }
`;

export const ContactActionsRow = styled(Box)`
  margin-top: auto;
  display: flex;
  align-items: stretch;
  gap: 8px;
`;

export const ContactBox = styled(Box)`
  flex: 1;
  min-width: 0;
  background: linear-gradient(90deg, #01ebb2 0%, #0d4efe 100%);
  color: white;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  font-weight: 600;
  letter-spacing: 0.02em;
  transition: transform 0.15s ease, box-shadow 0.2s ease, background 0.2s ease;

  &:hover {
    filter: brightness(1.05);
    box-shadow: 0 4px 14px rgba(13, 78, 254, 0.35);
    transform: translateY(-1px);
  }

  .MuiTypography-button {
    font-weight: 700;
    text-transform: none;
    font-size: 0.95rem;
  }

  .MuiSvgIcon-root {
    font-size: 16px;
  }
`;

export const CallBox = styled(Box)`
  flex: 1;
  min-width: 0;
  background: #ffffff;
  color: #0d4efe;
  border: 1.5px solid rgba(26, 26, 46, 0.14);
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  font-weight: 600;
  letter-spacing: 0.02em;
  transition:
    transform 0.15s ease,
    box-shadow 0.2s ease,
    background 0.2s ease,
    border-color 0.2s ease;

  &:hover {
    background: linear-gradient(
      135deg,
      rgba(1, 235, 178, 0.1) 0%,
      rgba(13, 78, 254, 0.08) 100%
    );
    border-color: #0d4efe;
    box-shadow: 0 4px 12px rgba(13, 78, 254, 0.15);
    transform: translateY(-1px);
  }

  .MuiTypography-button {
    font-weight: 700;
    text-transform: none;
    font-size: 0.95rem;
  }

  .MuiSvgIcon-root {
    font-size: 16px;
  }
`;